import { formatJst } from '@/lib/utils';
/**
 * AdminCommandPalette — global Cmd/Ctrl-K admin lookup.
 *
 * Exports:
 *   • AdminCommandResults  — reusable results list (used by palette + Live Accounts board)
 *   • AdminCommandPalette   — the Cmd-K modal (mounted once in DashboardLayout for admin)
 *
 * Secret-safe: backend returns only masked email / masked ****last4 card refs.
 * Quick actions: Open 360 · Cards · MEISAI · Report history · Run report (Test/Real).
 */
import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { authedFetch } from '@/lib/auth';
import { useLanguage } from '@/lib/i18n';
import { useFloatingCollision } from '@/lib/useFloatingCollision';
import {
  Search, X, Loader2, CreditCard, Wifi, FileText, Eye, FlaskConical,
  Play, ShieldAlert, CircleUser, CornerDownLeft,
  Gauge, Activity, Rocket, LayoutDashboard, Users, Building2, ShieldCheck,
  LifeBuoy, AlertTriangle, Wallet, Database, Shield, ClipboardCheck,
  Sparkles, Send, Mail, Image as ImageIcon, Languages, Settings, BookOpen,
  Crown, CalendarClock, ArrowRight, Compass,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// ── Admin/Host page catalog ────────────────────────────────────────────────
// Powers "search everything" — every destination in the admin/host area is
// indexed here so the palette can jump straight to a page by name, keyword,
// or URL. Mirrors the sidebar in DashboardLayout (kept in sync intentionally;
// this is the navigable source of truth for the global search).
const ADMIN_PAGES = [
  { path: '/admin', icon: Gauge, en: 'Command Center', ja: 'コマンドセンター', group: 'Dashboard', kw: 'home dashboard overview start' },
  { path: '/admin/report-activity', icon: Activity, en: 'Live Report Activity', ja: 'レポート実績トラッカー', group: 'Dashboard', kw: 'reports live tracker activity' },
  { path: '/admin/launch-status', icon: Rocket, en: 'Launch Status', ja: 'ローンチ状況', group: 'Dashboard', kw: 'launch go-live status' },
  { path: '/admin/metrics', icon: LayoutDashboard, en: 'All Metrics', ja: '指標・メトリクス', group: 'Dashboard', kw: 'metrics kpi stats analytics' },

  { path: '/admin/customers', icon: Users, en: 'Customers', ja: '顧客一覧', group: 'Customers', kw: 'customers users accounts people' },
  { path: '/admin/org-operations', icon: Building2, en: 'Org Operations', ja: '組織オペレーション', group: 'Customers', kw: 'org organization business operations' },
  { path: '/admin/account-type-requests', icon: ShieldCheck, en: 'Account Requests', ja: 'アカウント申請', group: 'Customers', kw: 'account requests upgrade approval' },
  { path: '/admin/business-applications', icon: Building2, en: 'Business Applications', ja: '法人のお申し込み', group: 'Customers', kw: 'business applications b2b apply' },
  { path: '/admin/diagnostic', icon: Search, en: 'Diagnostic 360', ja: '顧客360診断', group: 'Customers', kw: 'diagnostic 360 customer debug' },
  { path: '/admin/support-messages', icon: LifeBuoy, en: 'Support Messages', ja: 'サポート', group: 'Customers', kw: 'support tickets messages help contact' },
  { path: '/admin/visitors', icon: Activity, en: 'Visitors & Site Log', ja: '訪問者・サイトログ', group: 'Customers', kw: 'visitors traffic site log analytics' },
  { path: '/admin/closed-accounts', icon: AlertTriangle, en: 'Closed Accounts', ja: '閉鎖済みアカウント', group: 'Customers', kw: 'closed deleted accounts churn' },
  { path: '/admin/card-billing', icon: Wallet, en: 'Billing Preview', ja: '請求プレビュー', group: 'Customers', kw: 'billing invoice charge preview' },

  { path: '/admin/reports', icon: FileText, en: 'Reports', ja: 'レポート', group: 'Reports & Ops', kw: 'reports delivery toll' },
  { path: '/admin/issues', icon: AlertTriangle, en: 'Issues & Recovery', ja: '問題・リカバリー', group: 'Reports & Ops', kw: 'issues recovery errors failures' },
  { path: '/admin/scheduler-health', icon: CalendarClock, en: 'Scheduler Health', ja: 'スケジューラ健全性', group: 'Reports & Ops', kw: 'scheduler cron health', hostOnly: true },
  { path: '/admin/automation', icon: Activity, en: 'Automation Center', ja: '自動化設定', group: 'Reports & Ops', kw: 'automation auto schedule settings' },
  { path: '/admin/meisai-monitor', icon: Wifi, en: 'MEISAI Monitor', ja: 'MEISAIモニター', group: 'Reports & Ops', kw: 'meisai monitor connection nexco' },
  { path: '/admin/jobs', icon: Activity, en: 'Job Monitor', ja: 'ジョブモニター', group: 'Reports & Ops', kw: 'jobs queue worker monitor' },
  { path: '/admin/data-health', icon: Database, en: 'Data Health', ja: 'データ健全性', group: 'Reports & Ops', kw: 'data health database integrity' },
  { path: '/admin/report-failures', icon: AlertTriangle, en: 'Report Failures', ja: 'レポート失敗', group: 'Reports & Ops', kw: 'report failures errors' },
  { path: '/admin/report-simulator', icon: FlaskConical, en: 'Report Simulator', ja: 'レポートシミュレータ', group: 'Reports & Ops', kw: 'report simulator test preview' },
  { path: '/admin/quarantine', icon: ShieldAlert, en: 'Quarantine', ja: '検疫・隔離', group: 'Reports & Ops', kw: 'quarantine held blocked validation review' },

  { path: '/admin/security', icon: ShieldAlert, en: 'Security Cockpit', ja: 'セキュリティ・コックピット', group: 'Security', kw: 'security cockpit hygiene' },
  { path: '/admin/security-events', icon: Shield, en: 'Security Events', ja: 'セキュリティイベント', group: 'Security', kw: 'security events login alerts' },
  { path: '/admin/fraud-watch', icon: Shield, en: 'Fraud Watch', ja: '要確認利用モニタリング', group: 'Security', kw: 'fraud abuse watch suspicious' },
  { path: '/admin/abuse-review', icon: ShieldAlert, en: 'Abuse Review', ja: '不正・乱用レビュー', group: 'Security', kw: 'abuse review moderation' },
  { path: '/admin/audit', icon: FileText, en: 'Audit Logs', ja: '取扱履歴', group: 'Security', kw: 'audit logs history trail' },
  { path: '/admin/export-audit', icon: ShieldCheck, en: 'Export Audit', ja: 'エクスポート監査', group: 'Security', kw: 'export audit data' },
  { path: '/admin/registration-attempts', icon: ClipboardCheck, en: 'Registration Attempts', ja: 'カード登録の失敗', group: 'Security', kw: 'registration attempts card failures' },

  { path: '/admin/early-access', icon: Sparkles, en: 'Early Access Waitlist', ja: '先行受付待機リスト', group: 'Growth & Comms', kw: 'early access waitlist signup' },
  { path: '/admin/email-campaigns', icon: Send, en: 'Email Campaigns', ja: 'メールキャンペーン', group: 'Growth & Comms', kw: 'email campaigns marketing newsletter' },
  { path: '/admin/email-templates', icon: Mail, en: 'Email Templates', ja: 'メールテンプレート', group: 'Growth & Comms', kw: 'email templates editor' },
  { path: '/admin/emails', icon: Mail, en: 'Email Logs', ja: 'メールログ', group: 'Growth & Comms', kw: 'email logs delivery sent' },
  { path: '/admin/og-preview', icon: ImageIcon, en: 'OG Preview', ja: 'OGプレビュー', group: 'Growth & Comms', kw: 'og preview social meta image' },
  { path: '/admin/homepage-media', icon: ImageIcon, en: 'Homepage Media', ja: 'ホームページ素材', group: 'Growth & Comms', kw: 'homepage media images hero' },
  { path: '/admin/testimonial-translations', icon: Languages, en: 'Testimonial Translations', ja: 'お客様の声・翻訳', group: 'Growth & Comms', kw: 'testimonials translations reviews' },
  { path: '/admin/ad-creatives', icon: ImageIcon, en: 'Ad Creatives', ja: '広告クリエイティブ', group: 'Growth & Comms', kw: 'ad creatives review facebook' },

  { path: '/admin/federation', icon: Wifi, en: 'Federation (ETC Card)', ja: '連携API（ETCカード）', group: 'System', kw: 'federation etc card api japanetccard' },
  { path: '/admin/sync-status', icon: Activity, en: 'Sync Status', ja: '同期ステータス', group: 'System', kw: 'sync status jec nojo jtr' },
  { path: '/admin/launch-checklist', icon: Rocket, en: 'Launch Checklist', ja: 'ローンチチェックリスト', group: 'System', kw: 'launch checklist go-live ready' },
  { path: '/admin/billing-readiness', icon: CreditCard, en: 'Billing Readiness', ja: 'お支払い開始準備', group: 'System', kw: 'billing readiness stripe payments' },
  { path: '/admin/beta-operations', icon: Activity, en: 'Beta Ops', ja: 'ベータ運用', group: 'System', kw: 'beta operations ops' },
  { path: '/admin/launch-week-ops', icon: Rocket, en: 'Launch Week Ops', ja: 'ローンチ週運用', group: 'System', kw: 'launch week ops' },
  { path: '/admin/phase-b-live', icon: Play, en: 'Phase B Live', ja: 'フェーズBライブ', group: 'System', kw: 'phase b live test', hostOnly: true },
  { path: '/admin/meisai-live-accounts', icon: Activity, en: 'MEISAI Live Accounts', ja: 'MEISAIライブ口座', group: 'System', kw: 'meisai live accounts', hostOnly: true },
  { path: '/admin/workflow-playbook', icon: BookOpen, en: 'Workflow Playbook', ja: 'ワークフロー手順書', group: 'System', kw: 'workflow playbook guide docs' },
  { path: '/admin/settings', icon: Settings, en: 'Admin Settings', ja: '管理設定', group: 'System', kw: 'settings config preferences' },
  { path: '/admin/host-tools', icon: Crown, en: 'Host Tools', ja: 'ホストツール', group: 'System', kw: 'host tools owner super admin', hostOnly: true },
  { path: '/admin/exports', icon: FileText, en: 'Translation Exports', ja: '翻訳エクスポート', group: 'System', kw: 'translation exports i18n', hostOnly: true },
];

function searchPages(q, isHost) {
  const needle = q.trim().toLowerCase();
  if (needle.length < 2) return [];
  return ADMIN_PAGES.filter((p) => {
    if (p.hostOnly && !isHost) return false;
    return (
      p.en.toLowerCase().includes(needle) ||
      (p.ja || '').includes(q.trim()) ||
      p.path.toLowerCase().includes(needle) ||
      (p.kw || '').includes(needle)
    );
  }).slice(0, 7);
}

const HEALTH_CLS = {
  active: 'bg-emerald-100 text-emerald-700',
  trial: 'bg-blue-100 text-blue-700',
  paused: 'bg-amber-100 text-amber-700',
  unverified: 'bg-slate-100 text-slate-500',
  reconnect_required: 'bg-rose-100 text-rose-700',
};
const MEISAI_CLS = {
  active: 'text-emerald-600', pending: 'text-amber-600',
  error: 'text-rose-600', none: 'text-slate-400',
};

export function useCommandSearch() {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  // Debounced search. This effect sets loading/results state (both directly
  // for the reset path and inside the debounce timer), which trips the
  // set-state-in-effect rule; the pattern is intentional. Bare block-disable
  // is used because the rule name isn't known to the CRA build's ESLint.
  /* eslint-disable */
  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (q.trim().length < 2) { setResults([]); setLoading(false); return; }
    setLoading(true);
    timer.current = setTimeout(async () => {
      try {
        const r = await authedFetch(`${API}/api/admin/command/search?q=${encodeURIComponent(q.trim())}`, { credentials: 'include' });
        if (r.ok) { const d = await r.json(); setResults(d.results || []); }
      } catch { /* transient */ } finally { setLoading(false); }
    }, 250);
    return () => timer.current && clearTimeout(timer.current);
  }, [q]);
  /* eslint-enable */

  return { q, setQ, results, loading };
}

function RunReportMenu({ row, onDone }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const meisaiReady = row.meisai_status === 'active';

  const runTest = async () => {
    setBusy(true);
    try {
      const r = await authedFetch(`${API}/api/admin/customers/${row.user_id}/trigger-test-report`, {
        method: 'POST', credentials: 'include',
      });
      if (r.ok) toast.success(`TEST report queued for ${row.jtr_id} — sample data only`, { description: 'Clearly labeled as a test; not live proof.' });
      else toast.error('Could not queue test report');
    } catch { toast.error('Network error'); } finally { setBusy(false); setOpen(false); onDone?.(); }
  };

  return (
    <div className="relative">
      <button
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-semibold bg-violet-600 text-white hover:bg-violet-700"
        data-testid={`run-report-${row.user_id}`}
      >
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />} Run report
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-56 bg-white border border-slate-200 rounded-lg shadow-xl z-50 p-1" onClick={(e) => e.stopPropagation()}>
          <button
            disabled={!meisaiReady}
            onClick={() => { setOpen(false); navigate(`/admin/customers/${row.user_id}/360?action=real-report`); }}
            className={`w-full text-left px-2.5 py-2 rounded-md text-xs flex items-start gap-2 ${meisaiReady ? 'hover:bg-emerald-50 text-slate-800' : 'opacity-50 cursor-not-allowed text-slate-400'}`}
            data-testid={`real-report-${row.user_id}`}
          >
            <Play className="w-3.5 h-3.5 mt-0.5 text-emerald-600" />
            <span><span className="font-bold">Real MEISAI report</span><br /><span className="text-[10px]">{meisaiReady ? 'Live data pull · opens 360' : 'MEISAI not connected'}</span></span>
          </button>
          <button onClick={runTest} className="w-full text-left px-2.5 py-2 rounded-md text-xs flex items-start gap-2 hover:bg-amber-50 text-slate-800" data-testid={`test-report-${row.user_id}`}>
            <FlaskConical className="w-3.5 h-3.5 mt-0.5 text-amber-600" />
            <span><span className="font-bold">Test / sample report</span><br /><span className="text-[10px]">Instant · clearly labeled TEST</span></span>
          </button>
        </div>
      )}
    </div>
  );
}

export function AdminCommandResults({ results, loading, q, onNavigate }) {
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const ja = lang === 'ja';
  const go = (path) => { onNavigate?.(); navigate(path); };

  if (loading && results.length === 0) {
    return <div className="py-8 text-center text-slate-400 text-sm flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> {ja ? '検索中…' : 'Searching…'}</div>;
  }
  if (q.trim().length >= 2 && results.length === 0) {
    return <div className="py-8 text-center text-slate-400 text-sm">{ja ? `「${q}」に一致する結果はありません。` : `No matches for “${q}”.`}</div>;
  }
  return (
    <div className="divide-y divide-slate-100" data-testid="command-results">
      {results.map((r) => (
        <div
          key={r.user_id}
          onClick={() => go(`/admin/customers/${r.user_id}/360`)}
          className="px-3 py-3 hover:bg-slate-50 cursor-pointer flex items-center gap-3 group"
          data-testid={`command-result-${r.user_id}`}
        >
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${r.is_test_account ? 'bg-violet-100' : 'bg-slate-100'}`}>
            <CircleUser className={`w-5 h-5 ${r.is_test_account ? 'text-violet-600' : 'text-slate-500'}`} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-slate-900 text-sm truncate">{r.name || '—'}</span>
              {r.is_test_account && <span className="px-1.5 py-0.5 rounded text-[9px] font-black bg-violet-600 text-white uppercase tracking-wide">TEST · {r.test_tag}</span>}
              <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${HEALTH_CLS[r.account_health] || 'bg-slate-100 text-slate-500'}`}>{r.account_health}</span>
            </div>
            <div className="flex items-center gap-2.5 text-[11px] text-slate-500 mt-0.5 flex-wrap">
              <span className="font-mono text-violet-700">{r.jtr_id || '—'}</span>
              <span className="truncate">{r.email_masked}</span>
              <span className="inline-flex items-center gap-1"><CreditCard className="w-3 h-3" /> {r.card_ref_masked || '—'} · {r.cards_count}</span>
              <span className={`inline-flex items-center gap-1 ${MEISAI_CLS[r.meisai_status] || 'text-slate-400'}`}><Wifi className="w-3 h-3" /> {r.meisai_status}</span>
              <span className="inline-flex items-center gap-1"><FileText className="w-3 h-3" /> {r.last_report_at ? formatJst(r.last_report_at, { withTime: false }) : (ja ? 'レポートなし' : 'no reports')} · {r.schedule}</span>
            </div>
          </div>
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
            <button title={ja ? 'カードを表示' : 'View cards'} onClick={() => go(`/admin/customers/${r.user_id}/360?focus=cards`)} className="p-1.5 rounded-md hover:bg-slate-200 text-slate-600" data-testid={`act-cards-${r.user_id}`}><CreditCard className="w-3.5 h-3.5" /></button>
            <button title={ja ? 'MEISAI状態' : 'MEISAI status'} onClick={() => go(`/admin/customers/${r.user_id}/360?focus=meisai`)} className="p-1.5 rounded-md hover:bg-slate-200 text-slate-600" data-testid={`act-meisai-${r.user_id}`}><Wifi className="w-3.5 h-3.5" /></button>
            <button title={ja ? 'レポート履歴' : 'Report history'} onClick={() => go(`/admin/customers/${r.user_id}/360?focus=reports`)} className="p-1.5 rounded-md hover:bg-slate-200 text-slate-600" data-testid={`act-reports-${r.user_id}`}><FileText className="w-3.5 h-3.5" /></button>
            <button title={ja ? '360を開く' : 'Open 360'} onClick={() => go(`/admin/customers/${r.user_id}/360`)} className="p-1.5 rounded-md hover:bg-slate-200 text-slate-600" data-testid={`act-360-${r.user_id}`}><Eye className="w-3.5 h-3.5" /></button>
            <RunReportMenu row={r} />
          </div>
        </div>
      ))}
    </div>
  );
}

export function AdminCommandPalette({ isHost = false }) {
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  const fabRef = useRef(null);
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const ja = lang === 'ja';
  const { q, setQ, results, loading } = useCommandSearch();

  const close = useCallback(() => { setOpen(false); setQ(''); }, [setQ]);

  // Floating launcher must never sit on top of an open drawer, dialog, or
  // critical action menu. Observe the DOM and hide the FAB while any overlay
  // (role=dialog / aria-modal / the mobile nav drawer) is present.
  const [obscured, setObscured] = useState(false);
  useEffect(() => {
    const check = () => {
      const overlay = document.querySelector('[role="dialog"],[aria-modal="true"],[data-testid="mobile-nav-drawer"]');
      setObscured(!!overlay || document.body.classList.contains('drawer-open'));
    };
    check();
    const obs = new MutationObserver(check);
    obs.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'role', 'aria-modal'] });
    return () => obs.disconnect();
  }, []);

  const pageMatches = useMemo(() => searchPages(q, isHost), [q, isHost]);

  // Never cover an interactive content element at any scroll position.
  const colliding = useFloatingCollision(fabRef, { enabled: !open && !obscured });

  // Cmd/Ctrl-K opens the global search; Esc closes it. Also open via the
  // admin mobile top-bar search button (the floating FAB is hidden on phones).
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    const onOpenEvent = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('jtr:open-command-palette', onOpenEvent);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('jtr:open-command-palette', onOpenEvent);
    };
  }, []);

  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 50); }, [open]);

  const goPage = (path) => { close(); navigate(path); };

  if (!open) {
    if (obscured) return null;
    return (
      <button
        ref={fabRef}
        onClick={() => setOpen(true)}
        className={`fixed z-40 hidden sm:inline-flex items-center justify-center gap-2.5 rounded-full text-white shadow-lg shadow-indigo-500/30 text-sm font-semibold transition-all hover:shadow-xl hover:shadow-indigo-500/40 hover:-translate-y-0.5 h-12 w-12 sm:h-auto sm:w-auto sm:pl-4 sm:pr-3 sm:py-2.5 right-4 sm:right-5 ${colliding ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
        style={{ background: 'linear-gradient(135deg,#4f46e5,#7c3aed,#2563eb)', bottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))' }}
        data-testid="open-command-palette"
        data-floating-control="true"
        aria-hidden={colliding ? 'true' : undefined}
        tabIndex={colliding ? -1 : undefined}
        aria-label={ja ? '検索' : 'Search'}
        title={ja ? '検索' : 'Search'}
      >
        <Search className="w-5 h-5 sm:w-4 sm:h-4" />
        <span className="hidden sm:inline">{ja ? '検索' : 'Search'}</span>
        <kbd className="hidden sm:inline ml-0.5 px-1.5 py-0.5 rounded-md bg-white/20 text-white/90 font-mono text-[10px] leading-none">⌘K</kbd>
      </button>
    );
  }

  const showEmpty = q.trim().length < 2;
  const noMatches = !showEmpty && !loading && pageMatches.length === 0 && results.length === 0;

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[10vh] px-4" data-testid="command-palette">
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={close} />
      <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl ring-1 ring-slate-900/5 overflow-hidden">
        {/* Colorful accent strip */}
        <div className="h-1 w-full" style={{ background: 'linear-gradient(90deg,#4f46e5,#7c3aed,#2563eb,#06b6d4)' }} />

        {/* Search field */}
        <div className="flex items-center gap-3 px-4 py-3.5 border-b border-slate-100">
          <span className="flex items-center justify-center w-9 h-9 rounded-xl bg-indigo-50 shrink-0">
            <Search className="w-5 h-5 text-indigo-600" />
          </span>
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={ja ? 'ページ・顧客・設定を検索…' : 'Search pages, customers, settings…'}
            className="flex-1 text-base text-slate-900 bg-transparent outline-none border-0 ring-0 focus:ring-0 placeholder:text-slate-400"
            data-testid="command-input"
          />
          {loading && <Loader2 className="w-4 h-4 animate-spin text-indigo-400" />}
          <button onClick={close} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors" aria-label="Close">
            <X className="w-4 h-4 text-slate-400" />
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {showEmpty ? (
            <div className="px-4 py-10 text-center space-y-3">
              <span className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-50 to-violet-50">
                <Compass className="w-6 h-6 text-indigo-500" />
              </span>
              <p className="text-sm font-semibold text-slate-700">
                {ja ? '管理エリア全体を検索' : 'Search the entire admin area'}
              </p>
              <p className="text-xs text-slate-400 max-w-sm mx-auto leading-relaxed">
                {ja
                  ? 'ページ名・設定・顧客（氏名・メール・JTR-ID・ETCカード番号）を入力してジャンプ。'
                  : 'Jump to any page or setting, or find a customer by name, email, JTR-ID, or ETC card digits.'}
              </p>
            </div>
          ) : noMatches ? (
            <div className="py-10 text-center text-slate-400 text-sm">
              {ja ? `「${q}」に一致する結果はありません。` : `No matches for "${q}".`}
            </div>
          ) : (
            <div>
              {/* Pages section */}
              {pageMatches.length > 0 && (
                <div className="pt-2">
                  <p className="px-4 pt-1 pb-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    {ja ? 'ページ・設定' : 'Pages & Settings'}
                  </p>
                  <div data-testid="command-page-results">
                    {pageMatches.map((p) => {
                      const Icon = p.icon || ArrowRight;
                      return (
                        <button
                          key={p.path}
                          onClick={() => goPage(p.path)}
                          className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-indigo-50/60 transition-colors group text-left"
                          data-testid={`command-page-${p.path}`}
                        >
                          <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-indigo-100 text-indigo-600 shrink-0 group-hover:bg-indigo-600 group-hover:text-white transition-colors">
                            <Icon className="w-4 h-4" />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm font-semibold text-slate-800 truncate">{ja ? p.ja : p.en}</span>
                            <span className="block text-[11px] text-slate-400 font-mono truncate">{p.path}</span>
                          </span>
                          <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-indigo-500 transition-colors shrink-0" />
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Customers section */}
              {(loading || results.length > 0) && (
                <div className="pt-2">
                  <p className="px-4 pt-1 pb-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                    {ja ? '顧客' : 'Customers'}
                    <ShieldAlert className="w-3 h-3 text-slate-300" />
                    <span className="font-medium normal-case tracking-normal text-slate-300">
                      {ja ? 'カード検索はマスク・監査記録' : 'card searches masked & audit-logged'}
                    </span>
                  </p>
                  <AdminCommandResults results={results} loading={loading} q={q} onNavigate={close} />
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-4 py-2.5 border-t border-slate-100 bg-slate-50/60 text-[11px] text-slate-400 flex items-center gap-4">
          <span className="inline-flex items-center gap-1"><CornerDownLeft className="w-3 h-3" /> {ja ? '開く' : 'open'}</span>
          <span className="inline-flex items-center gap-1"><kbd className="px-1.5 py-0.5 rounded bg-white border border-slate-200 font-mono">⌘K</kbd> {ja ? '検索' : 'search'}</span>
          <span className="inline-flex items-center gap-1"><kbd className="px-1.5 py-0.5 rounded bg-white border border-slate-200 font-mono">esc</kbd> {ja ? '閉じる' : 'close'}</span>
        </div>
      </div>
    </div>,
    document.body,
  );
}
